import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AnnotationLayer, LiveFeed, Player, Timeline, type TimedEvent } from "@backstory/player";
import type { AgentToUser, AnnotationShape, ControlCommand, LiveAssistSession, LiveCapability, UserToAgent } from "@backstory/protocol";
import { api, ApiRequestError } from "../../api";
import { ErrorState } from "../../components/States";
import { PageHeader, useToast } from "../../components/ui";
import { formatDuration } from "../../lib/format";
import { DevToolsDock, usePanelActions, usePanelFetchers } from "../devtools";
import { askWhy, useAskWhy } from "../ai";
import { agentSocketProtocols, agentSocketUrl, liveApi } from "./api";
import {
  addAgentChat,
  addPendingCommand,
  arrowShape,
  clickCommand,
  highlightShape,
  initialState,
  inputCommand,
  isGranted,
  keyCommand,
  laserShape,
  markRequested,
  markRevokedByAgent,
  newShapeId,
  normalizePoint,
  penShape,
  policyBlocks,
  rectShape,
  reduce,
  scrollCommand,
  scrollByCommand,
  textShape,
  type LiveState,
} from "./model";
import { LiveAgentSocket, type SocketStatus } from "./socket";
import { connectVoice, listMicrophones, type VoiceMicrophone, type VoiceRoom } from "./voice";
import { AssistPanel, CaptureChip, CaptureHealthCard, ChatBox, ControlResults, Copilot, LiveEventsPanel, LiveSessionUnavailable, NotLiveNotice, RailIcon, StageToolbar, SummaryDialog, type AnnotationTool } from "./components";
import { applyLiveChunk, LIVE_STALE_MS, type LiveCaptureHealth } from "../../lib/capture";

type Action = { type: "msg"; msg: UserToAgent } | { type: "requested"; cap: LiveCapability; requestId: string } | { type: "revoke"; cap: LiveCapability } | { type: "chat"; text: string } | { type: "pending"; commandId: string; command: ControlCommand };

function reducer(state: LiveState, a: Action): LiveState {
  switch (a.type) {
    case "msg":
      return reduce(state, a.msg);
    case "requested":
      return markRequested(state, a.cap, a.requestId);
    case "revoke":
      return markRevokedByAgent(state, a.cap);
    case "chat":
      return addAgentChat(state, a.text);
    case "pending":
      return addPendingCommand(state, a.commandId, a.command);
  }
}

/**
 * How long a freshly opened console waits for its first chunk before saying the
 * page is not live. The SDK is asked for a checkpoint on attach, so a page that
 * is really there answers well inside this; a page that is already gone never
 * will, and no presence transition exists to announce what happened before we
 * arrived.
 */
const FIRST_CHUNK_GRACE_MS = 12_000;

/** Test seam: components can read the live Player/feed through this attribute. */
export interface LiveConsoleHandles {
  player: Player | null;
  feed: LiveFeed | null;
  socket: LiveAgentSocket | null;
}

export function LiveConsolePage() {
  const { sessionId } = useParams({ strict: false }) as { sessionId: string };
  const toast = useToast();

  const [state, dispatch] = useReducer(reducer, undefined, initialState);
  const [status, setStatus] = useState<SocketStatus>("closed");
  /** When our socket opened, so silence before the first chunk can be judged too. */
  const [openedAt, setOpenedAt] = useState(0);
  const [reconnect, setReconnect] = useState(0);
  const [version, setVersion] = useState(0);
  const [time, setTime] = useState(0);
  const [following, setFollowing] = useState(true);
  const [tool, setTool] = useState<AnnotationTool>("none");
  const [controlOn, setControlOn] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [capture, setCapture] = useState<LiveCaptureHealth | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [priorEvents, setPriorEvents] = useState<readonly TimedEvent[]>([]);
  const [assist, setAssist] = useState<LiveAssistSession | undefined>();
  const [voice, setVoice] = useState<{ room: VoiceRoom; muted: boolean; level: number; participants: number } | null>(null);
  const [voiceError, setVoiceError] = useState<string | undefined>();
  const [voiceOff, setVoiceOff] = useState(false);
  const voiceProbe = useQuery({
    queryKey: ["live", "voice-available"],
    queryFn: () => liveApi.voiceAvailable(),
    retry: 0,
    staleTime: 60_000,
  });
  const voiceUnavailable = voiceOff || (voiceProbe.isSuccess && !voiceProbe.data.available);
  const [voiceAttempt, setVoiceAttempt] = useState(0);
  const [microphones, setMicrophones] = useState<VoiceMicrophone[]>([]);
  const [microphoneId, setMicrophoneId] = useState("default");
  const [stageHeight, setStageHeight] = useState(480);
  const [textDraft, setTextDraft] = useState<{ x: number; y: number } | null>(null);
  const [textValue, setTextValue] = useState("");
  const startedAt = useRef(Date.now());

  const stageRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const feedRef = useRef<LiveFeed | null>(null);
  const layerRef = useRef<AnnotationLayer | null>(null);
  const socketRef = useRef<LiveAgentSocket | null>(null);
  const controlDocRef = useRef<Document | null>(null);
  const detachControlRef = useRef<(() => void) | null>(null);
  const replayScrollsRef = useRef(new Map<number, { top: number; left: number; at: number }>());

  // ---- start the assist session and open the socket
  const start = useMutation({
    mutationFn: () => liveApi.start(sessionId),
    onSuccess: (a) => setAssist(a),
    onError: (e: Error) => toast.push("error", e.message),
  });
  useEffect(() => {
    startedAt.current = Date.now();
    start.mutate();
    const stage = stageRef.current;
    if (!stage) return;
    const player = new Player(stage, { fit: true, showUrlBar: false });
    playerRef.current = player;
    const feed = new LiveFeed(player, { lagMs: 0 });
    feedRef.current = feed;
    // Draw in the Player's recorded-viewport coordinate space. The outer stage
    // can contain letterboxing when the customer's viewport aspect ratio differs
    // from the console, so using it would offset/scatter annotations.
    const viewportStage = player.mirror.iframe.parentElement ?? stage;
    const layer = new AnnotationLayer(viewportStage, { resolveBox: (id) => player.boxOf(id) });
    layerRef.current = layer;
    const offs = [
      player.on("timeupdate", ({ currentTime }) => setTime(currentTime)),
      feed.on("chunk", () => setVersion((v) => v + 1)),
      feed.on("follow", ({ following }) => setFollowing(following)),
      player.on("event", ({ event }) => {
        if (event.type === "scroll") replayScrollsRef.current.set(event.id, { top: event.y, left: event.x, at: performance.now() });
      }),
    ];
    (stage as HTMLDivElement & { __rwLive?: LiveConsoleHandles }).__rwLive = { player, feed, socket: null };
    return () => {
      offs.forEach((off) => off());
      detachControlRef.current?.();
      layer.destroy();
      feed.destroy();
      player.destroy();
      playerRef.current = null;
      feedRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // The agent socket is rejected until the assist record exists, so it waits for
  // `start` instead of racing it: a socket that raced would burn its reconnect
  // attempts on 409s and strand the console with no stream and no way back.
  useEffect(() => {
    const feed = feedRef.current;
    if (!assist?.id || !feed) return;
    const socket = new LiveAgentSocket(agentSocketUrl(sessionId), undefined, agentSocketProtocols());
    socketRef.current = socket;
    socket.on("status", (s) => {
      setStatus(s);
      if (s === "open") setOpenedAt(Date.now());
    });
    socket.on("message", (msg) => {
      dispatch({ type: "msg", msg });
      if (msg.type === "ended" && (msg.reason === "minutes" || msg.reason === "unavailable")) socket.close();
    });
    socket.on("chunk", (chunk) => {
      feed.append(chunk);
      // Folded from the same chunks the player consumes: the recorder's health
      // is already on this socket, so nothing extra is fetched to show it.
      setCapture((prev) => applyLiveChunk(prev, chunk));
    });
    socket.on("error", () => undefined);
    socket.connect();
    const stage = stageRef.current as (HTMLDivElement & { __rwLive?: LiveConsoleHandles }) | null;
    if (stage?.__rwLive) stage.__rwLive.socket = socket;
    return () => {
      socket.close();
      socketRef.current = null;
      if (stage?.__rwLive) stage.__rwLive.socket = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, assist?.id, reconnect]);

  // Clock for countdowns.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const md = navigator.mediaDevices;
    let active = true;
    const refresh = () => void listMicrophones(md).then((devices) => {
      if (!active) return;
      setMicrophones(devices);
      setMicrophoneId((current) => devices.some((d) => d.deviceId === current) ? current : "default");
    }).catch(() => undefined);
    refresh();
    md?.addEventListener?.("devicechange", refresh);
    return () => {
      active = false;
      md?.removeEventListener?.("devicechange", refresh);
    };
  }, []);

  // Keep the viewer close to the customer's viewport aspect ratio. A fixed
  // short stage forces the Player to shrink against height and makes the page
  // look unlike the source even when plenty of horizontal room is available.
  useEffect(() => {
    const el = stageRef.current;
    const wrap = el?.parentElement;
    if (!el || !wrap || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const aspect = state.viewport?.w && state.viewport?.h ? state.viewport.h / state.viewport.w : 9 / 16;
      const ideal = Math.round(el.clientWidth * aspect);
      // The console fills the viewport, so the stage never grows past the room
      // the layout gives it: a taller stage would push the dock off-screen.
      const available = wrap.clientHeight;
      setStageHeight(available > 0 ? Math.max(200, Math.min(ideal, available)) : Math.max(320, Math.min(900, ideal)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [state.viewport?.w, state.viewport?.h]);

  // Session ended by the user/tab.
  useEffect(() => {
    if (state.ended) {
      setControlOn(false);
      if (state.ended.reason !== "minutes" && state.ended.reason !== "unavailable") setSummaryOpen(true);
    }
  }, [state.ended]);

  const send = useCallback((msg: AgentToUser) => socketRef.current?.send(msg), []);

  // ---- capability requests
  const request = useMutation({
    mutationFn: async (cap: LiveCapability) => {
      const { requestId } = await liveApi.requestCapability(sessionId, cap);
      return { cap, requestId };
    },
    onSuccess: ({ cap, requestId }) => {
      dispatch({ type: "requested", cap, requestId });
    },
    onError: (e: Error) => {
      if (e.message.toLowerCase().includes("not configured")) setVoiceOff(true);
      toast.push("error", e.message);
    },
  });
  const revoke = (cap: LiveCapability) => {
    send({ type: "revoke", capability: cap });
    dispatch({ type: "revoke", cap });
    if (cap === "control" || cap === "cobrowse") setControlOn(false);
    if (cap === "annotate" || cap === "cobrowse") setTool("none");
    if (cap === "voice") void leaveVoice();
  };

  // ---- voice: join the LiveKit room when granted
  const leaveVoice = useCallback(async () => {
    const v = voice;
    setVoice(null);
    await v?.room.leave().catch(() => undefined);
  }, [voice]);
  useEffect(() => {
    if (!isGranted(state, "voice") || voice || !assist?.id) return;
    let cancelled = false;
    (async () => {
      try {
        setVoiceError(undefined);
        const tok = await liveApi.voiceToken(assist.id);
        const room = await connectVoice(tok.url, tok.token, microphoneId);
        if (cancelled) {
          await room.leave();
          return;
        }
        setVoice({ room, muted: false, level: 0, participants: 0 });
        room.onLevel((level) => setVoice((v) => (v ? { ...v, level } : v)));
        room.onParticipants((participants) => setVoice((v) => (v ? { ...v, participants } : v)));
        setVoiceError(undefined);
      } catch (e) {
        setVoiceError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.rungs.voice, assist?.id, voiceAttempt, microphoneId]);
  useEffect(() => {
    if (state.rungs.voice !== "granted" && voice) void leaveVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.rungs.voice]);

  // ---- annotations
  const annotateOk = isGranted(state, "annotate");
  const sendShape = useCallback(
    (shape: AnnotationShape) => {
      send({ type: "annotation", shape });
      layerRef.current?.add(shape);
    },
    [send],
  );
  /** The transformed rectangle occupied by the recorded viewport (no letterbox). */
  const stageRect = () => (playerRef.current?.mirror.iframe.getBoundingClientRect() ?? stageRef.current?.getBoundingClientRect() ?? { left: 0, top: 0, width: 1, height: 1 }) as DOMRect;
  const insideStage = (e: React.PointerEvent) => {
    const r = stageRect();
    return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
  };
  const draftPosition = (p: { x: number; y: number }) => {
    const viewport = stageRect();
    const wrap = overlayRef.current?.getBoundingClientRect();
    return {
      left: viewport.left - (wrap?.left ?? 0) + p.x * viewport.width,
      top: viewport.top - (wrap?.top ?? 0) + p.y * viewport.height,
    };
  };
  const drawing = useRef<{
    id: string;
    kind: "pen" | "arrow" | "rect";
    start: { x: number; y: number };
    points: Array<{ x: number; y: number }>;
    lastSentAt: number;
  } | null>(null);
  const shapeForDrawing = (d: NonNullable<typeof drawing.current>): AnnotationShape => {
    if (d.kind === "pen") return { ...penShape(d.points), id: d.id };
    const to = d.points[d.points.length - 1] ?? d.start;
    if (d.kind === "arrow") return { ...arrowShape(d.start, to), id: d.id };
    return { ...rectShape(d.start, to), id: d.id };
  };
  const onPointerDown = (e: React.PointerEvent) => {
    if (!annotateOk || tool === "none" || tool === "highlight" || !insideStage(e)) return;
    const p = normalizePoint(e.clientX, e.clientY, stageRect());
    if (tool === "laser") {
      sendShape(laserShape(p));
      return;
    }
    if (tool === "text") {
      setTextDraft(p);
      return;
    }
    drawing.current = { id: newShapeId(tool), kind: tool, start: p, points: [p], lastSentAt: Number.NEGATIVE_INFINITY };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!annotateOk) return;
    const p = normalizePoint(e.clientX, e.clientY, stageRect());
    if (tool === "laser" && e.buttons > 0) {
      sendShape(laserShape(p));
      return;
    }
    const d = drawing.current;
    if (!d) return;
    if (d.kind === "pen") d.points.push(p);
    else d.points = [d.start, p];
    // Reuse one shape id so both canvases replace the in-progress geometry
    // instead of leaving a trail of independent 10-second annotations.
    // 30fps is immediate to the eye without flooding the relay/recording.
    const at = performance.now();
    if (at - d.lastSentAt >= 33) {
      d.lastSentAt = at;
      sendShape(shapeForDrawing(d));
    }
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const p = normalizePoint(e.clientX, e.clientY, stageRect());
    const d = drawing.current;
    drawing.current = null;
    const last = d.points[d.points.length - 1];
    if (!last || last.x !== p.x || last.y !== p.y) {
      if (d.kind === "pen") d.points.push(p);
      else d.points = [d.start, p];
    }
    // Always send the final geometry; the last pointer move may have landed
    // inside the throttle window.
    sendShape(shapeForDrawing(d));
  };
  // Highlight tool: pick an element by clicking it inside the mirrored document.
  // Listeners re-attach whenever a chunk arrives (the document may be rebuilt on a snapshot).
  useEffect(() => {
    const player = playerRef.current;
    if (!player || tool !== "highlight" || !annotateOk) return;
    const doc = player.mirror.iframe.contentDocument;
    if (!doc) return;
    let hover: number | null = null;
    const onMove = (e: MouseEvent) => {
      const id = e.target ? player.mirror.getId(e.target as Node) : undefined;
      if (id == null || id === hover) return;
      hover = id;
      player.highlightNode(id, { ms: 400, color: "#f97316" });
    };
    const onClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const id = e.target ? player.mirror.getId(e.target as Node) : undefined;
      if (id == null) return;
      sendShape(highlightShape(id));
      player.highlightNode(id, { ms: 10_000, color: "#f97316" });
    };
    doc.addEventListener("mousemove", onMove, true);
    doc.addEventListener("click", onClick, true);
    return () => {
      doc.removeEventListener("mousemove", onMove, true);
      doc.removeEventListener("click", onClick, true);
      player.clearHighlight();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, annotateOk, sendShape, version]);

  // ---- remote control: translate agent gestures in the mirror into commands
  const sendCommand = useCallback(
    (command: ControlCommand) => {
      const commandId = newShapeId("cmd");
      dispatch({ type: "pending", commandId, command });
      send({ type: "control", command, commandId });
    },
    [send],
  );
  const controlActive = controlOn && isGranted(state, "control") && !(state.userActiveUntil && state.userActiveUntil > now);
  const highlightActive = tool === "highlight" && annotateOk;
  // The replay iframe is inert everywhere else. Temporarily enable hit testing
  // for element highlighting or remote control, then restore the safe default.
  useEffect(() => {
    const iframe = playerRef.current?.mirror.iframe;
    if (!iframe) return;
    iframe.style.pointerEvents = controlActive || highlightActive ? "auto" : "none";
    return () => {
      iframe.style.pointerEvents = "none";
    };
  }, [controlActive, highlightActive, version]);
  useEffect(() => {
    detachControlRef.current?.();
    detachControlRef.current = null;
    const player = playerRef.current;
    if (!controlActive || highlightActive || !player) return;
    const doc = player.mirror.iframe.contentDocument;
    if (!doc) return;
    controlDocRef.current = doc;
    const onClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const el = e.target as Element | null;
      const id = el ? player.mirror.getId(el) : undefined;
      if (id == null) return;
      const why = policyBlocks(el);
      if (why) {
        toast.push("error", `Not sent: ${why}.`);
        return;
      }
      // Events inside a transformed iframe report coordinates in the iframe's
      // unscaled viewport, while getBoundingClientRect() reports its fitted
      // on-screen size. Normalize against the former.
      sendCommand(clickCommand(id, normalizePoint(e.clientX, e.clientY, {
        left: 0,
        top: 0,
        width: doc.documentElement.clientWidth,
        height: doc.documentElement.clientHeight,
      })));
    };
    const onInput = (e: Event) => {
      const el = e.target as HTMLInputElement | null;
      const id = el ? player.mirror.getId(el) : undefined;
      if (id == null || policyBlocks(el)) return;
      sendCommand(inputCommand(id, el!.value ?? ""));
    };
    const onKey = (e: KeyboardEvent) => {
      if (["Enter", "Escape", "Tab", "ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        const el = e.target as Element | null;
        const id = el ? player.mirror.getId(el) : undefined;
        sendCommand(keyCommand(e.key, id ?? undefined));
      }
    };
    // The mirror is a replay: its own scroll position is written by incoming
    // events, so listening for `scroll` would echo the user's scrolls back at
    // them. Drive their page from wheel deltas instead, and let the resulting
    // scroll event come back through the stream to move the mirror.
    const scroller = (el: Element | null): Element | null => {
      const view = doc.defaultView;
      for (let n = el; n && n !== doc.documentElement && n !== doc.body; n = n.parentElement) {
        const overflow = view?.getComputedStyle(n).overflowY ?? "";
        if ((overflow === "auto" || overflow === "scroll") && n.scrollHeight > n.clientHeight + 1) return n;
      }
      return null;
    };
    let pending: { id: number; top: number; left: number } | null = null;
    let flushAt: ReturnType<typeof setTimeout> | null = null;
    let scrollbarPending: { id: number; top: number; left: number } | null = null;
    let scrollbarFlushAt: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      flushAt = null;
      const p = pending;
      pending = null;
      if (p && (p.top || p.left)) sendCommand(scrollByCommand(p.id, Math.round(p.top), Math.round(p.left)));
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? doc.documentElement.clientHeight : 1;
      const inner = scroller(e.target as Element | null);
      const id = (inner ? player.mirror.getId(inner) : 0) ?? 0;
      if (pending && pending.id !== id) flush();
      pending = { id, top: (pending?.top ?? 0) + e.deltaY * unit, left: (pending?.left ?? 0) + e.deltaX * unit };
      if (!flushAt) flushAt = setTimeout(flush, 60);
    };
    const onScroll = (e: Event) => {
      const target = e.target;
      const root = target === doc || target === doc.documentElement || target === doc.body;
      const el = !root && target && "nodeType" in target && target.nodeType === 1 ? (target as Element) : null;
      const id = root ? 0 : el ? (player.mirror.getId(el) ?? -1) : -1;
      if (id < 0) return;
      const top = root ? (doc.scrollingElement?.scrollTop ?? doc.documentElement.scrollTop) : (el as HTMLElement).scrollTop;
      const left = root ? (doc.scrollingElement?.scrollLeft ?? doc.documentElement.scrollLeft) : (el as HTMLElement).scrollLeft;
      const replayed = replayScrollsRef.current.get(id);
      if (replayed && performance.now() - replayed.at < 500 && Math.abs(replayed.top - top) < 2 && Math.abs(replayed.left - left) < 2) {
        replayScrollsRef.current.delete(id);
        return;
      }
      // A native scrollbar drag changes the mirror itself. Forward that
      // absolute thumb position; wheel gestures continue to use relative deltas.
      scrollbarPending = { id, top, left };
      if (!scrollbarFlushAt) {
        scrollbarFlushAt = setTimeout(() => {
          scrollbarFlushAt = null;
          const p = scrollbarPending;
          scrollbarPending = null;
          if (p) sendCommand(scrollCommand(p.id, Math.round(p.top), Math.round(p.left)));
        }, 60);
      }
    };
    doc.addEventListener("click", onClick, true);
    doc.addEventListener("input", onInput, true);
    doc.addEventListener("keydown", onKey, true);
    doc.addEventListener("wheel", onWheel, { capture: true, passive: false });
    doc.addEventListener("scroll", onScroll, true);
    detachControlRef.current = () => {
      doc.removeEventListener("click", onClick, true);
      doc.removeEventListener("input", onInput, true);
      doc.removeEventListener("keydown", onKey, true);
      doc.removeEventListener("wheel", onWheel, true);
      doc.removeEventListener("scroll", onScroll, true);
      if (flushAt) clearTimeout(flushAt);
      if (scrollbarFlushAt) clearTimeout(scrollbarFlushAt);
    };
    return () => {
      detachControlRef.current?.();
      detachControlRef.current = null;
    };
    // Re-attach when the mirror document changes (new snapshot) — version bumps on every chunk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlActive, highlightActive, version, sendCommand]);
  useEffect(() => {
    if (state.rungs.control !== "granted") setControlOn(false);
  }, [state.rungs.control]);

  const guideUser = (nodeId: number) => {
    sendShape(highlightShape(nodeId, "#22c55e"));
    send({ type: "chat", text: "Please click the highlighted element." });
    dispatch({ type: "chat", text: "Please click the highlighted element." });
  };

  // ---- chat and copilot
  const chat = (text: string) => {
    send({ type: "chat", text });
    dispatch({ type: "chat", text });
  };
  const ask = useAskWhy();
  const lastAssistant = [...ask.messages].reverse().find((m) => m.role === "assistant");
  const suggest = () => {
    askWhy.setContext({ sessionId });
    void askWhy.send("I am assisting this user live. Based on the latest events and issues, what is the most likely cause of their problem and what should I do next? Be brief and cite steps.");
  };

  // ---- end session
  const end = useMutation({
    mutationFn: async () => {
      send({ type: "end" });
      const a = await liveApi.end(sessionId).catch(() => undefined);
      return a;
    },
    onSuccess: (a) => {
      if (a) setAssist(a);
      dispatch({ type: "msg", msg: { type: "ended", reason: "agent" } });
      setSummaryOpen(true);
      void leaveVoice();
    },
  });

  const player = playerRef.current;
  const hasFrame = player?.renderable ?? false;
  const panelActions = usePanelActions(playerRef, player?.startTs ?? 0);
  const panelFetchers = usePanelFetchers(sessionId);
  const liveEdge = feedRef.current?.liveEdgeMs ?? 0;

  // Nothing to render yet: ask the page for a checkpoint rather than wait for
  // its scheduled one. The gateway's ingest-ack nudge only fires for the first
  // console to attach, so a reopened tab would otherwise sit on an empty stage
  // for minutes while events streamed in behind it.
  useEffect(() => {
    if (status !== "open" || hasFrame || state.ended) return;
    const ask = () => send({ type: "snapshot_request" });
    ask();
    const t = setInterval(ask, 5000);
    return () => clearInterval(t);
  }, [status, hasFrame, state.ended, send]);

  // Read back what the user did before anyone was watching. The live stream only
  // starts at attach, so a request made while they waited for help — usually the
  // very thing they need help with — would otherwise show up nowhere.
  useEffect(() => {
    if (status !== "open") return;
    let cancelled = false;
    void (async () => {
      try {
        const detail = await api.session(sessionId);
        const refs = [...(detail.chunks ?? [])].sort((a, b) => a.seq - b.seq);
        if (!refs.length) return;
        const timeline = new Timeline();
        // Newest first, so the traffic just before the agent arrived lands even
        // if a long session hits the budget.
        let budget = 6 * 1024 * 1024;
        for (const ref of refs.reverse()) {
          if (cancelled) return;
          budget -= ref.size;
          if (budget < 0) break;
          try {
            timeline.addChunk(await api.chunk(sessionId, ref.seq));
          } catch {
            // A chunk still in flight to storage is normal on a live session.
          }
        }
        if (!cancelled) setPriorEvents(timeline.events);
      } catch {
        // History is an enhancement; the live stream stands on its own.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, sessionId]);

  // A session that is gone is the expected outcome of a stale link, not a
  // failure to render the console, so it replaces the console rather than
  // appearing as an alert above an empty stage.
  const startStatus = start.error instanceof ApiRequestError ? start.error.status : undefined;
  if (startStatus === 404 || startStatus === 403) {
    return <LiveSessionUnavailable sessionId={sessionId} forbidden={startStatus === 403} onRetry={() => start.mutate()} />;
  }

  // Our socket to the API stays open long after the recorded page stops sending,
  // so it can never be the evidence of liveness. Three things can be:
  // the relay's presence signal (a closed tab, known at once), silence since the
  // last chunk, and — for a console that attached after the page was already
  // gone, which no transition announces — nothing at all since we connected.
  // The console keeps its socket regardless: a hidden or offline page resumes
  // the same session, so this is reported rather than acted on.
  const recorderSilent =
    state.userOnline === false ||
    (capture ? now - capture.lastChunkAt > LIVE_STALE_MS : status === "open" && openedAt > 0 && now - openedAt > FIRST_CHUNK_GRACE_MS);
  const headerBadge = state.ended
    ? { tone: "neutral", label: "ended" }
    : status !== "open"
      ? { tone: status === "failed" ? "danger" : "neutral", label: status }
      : recorderSilent
        ? { tone: "warn", label: "not live" }
        : { tone: "ok", label: "connected" };

  return (
    <div className="live-page">
      <PageHeader
        back={<Link to="/live">← Live</Link>}
        title={
          <span className="live-title">
            Live Assist <span className="mono muted small">{sessionId.slice(0, 10)}…</span>
            <span className={`badge ${headerBadge.tone}`} data-testid="socket-status">
              {headerBadge.label}
            </span>
            {status === "failed" && (
              <button
                className="btn small"
                onClick={() => (assist ? setReconnect((n) => n + 1) : start.mutate())}
                data-testid="socket-reconnect"
              >
                Reconnect
              </button>
            )}
            {state.helpCode && <span className="badge info">help code {state.helpCode}</span>}
            {state.url && (
              <span className="muted small live-header-url" title={state.url}>
                {state.url}
              </span>
            )}
          </span>
        }
        actions={
          <>
            <button className="btn ghost" onClick={() => setSummaryOpen(true)} disabled={!state.ended}>
              Summary
            </button>
            <button className="btn danger" onClick={() => end.mutate()} disabled={!!state.ended || end.isPending} data-testid="end-session">
              End session
            </button>
          </>
        }
      />
      {start.isError && <ErrorState error={start.error} retry={() => start.mutate()} />}
      {(recorderSilent || state.ended) && <NotLiveNotice sessionId={sessionId} ended={!!state.ended} reason={state.ended?.reason} />}
      <div className="live-layout">
        <div className="live-main">
          <div className={`live-stage-wrap ${controlActive ? "controlling" : ""}`}>
            <div className="live-stage" ref={stageRef} style={{ height: stageHeight }} data-testid="live-stage" />
            {!hasFrame && !state.ended && (
              <div className="live-stage-gate" data-testid="awaiting-frame">
                <strong>Waiting for a fresh page snapshot…</strong>
                <span className="muted small">
                  {status === "open" ? "Live events are connected and the page has been asked for a snapshot; the view appears as soon as it arrives." : "Connecting to the live stream…"}
                </span>
              </div>
            )}
            <div
              ref={overlayRef}
              className={`live-overlay ${tool !== "none" && tool !== "highlight" && annotateOk ? "armed" : ""}`}
              data-testid="annotation-overlay"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            />
            {textDraft && (
              <form
                className="live-text-draft"
                style={draftPosition(textDraft)}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (textValue.trim()) sendShape(textShape(textDraft, textValue.trim()));
                  setTextDraft(null);
                  setTextValue("");
                }}
                autoComplete="off"
              >
                <input autoFocus value={textValue} onChange={(e) => setTextValue(e.target.value)} aria-label="Annotation text" placeholder="Type, then Enter" autoComplete="off" />
              </form>
            )}
            <StageToolbar
              tool={tool}
              onTool={setTool}
              onClear={() => (send({ type: "annotation_clear" }), layerRef.current?.clear())}
              disabled={!annotateOk}
              controlGranted={isGranted(state, "control")}
              controlOn={controlOn}
              onControl={setControlOn}
            />
          </div>
          <div className="live-controls">
            <span className={`badge ${following ? "danger live" : "neutral"}`} data-testid="live-badge">
              {following ? "LIVE" : `${formatDuration(Math.max(0, liveEdge - time))} behind`}
            </span>
            <CaptureChip health={capture} now={now} offline={state.userOnline === false} />
            <button className="btn ghost small" onClick={() => (following ? feedRef.current?.pause() : feedRef.current?.backToLive())} data-testid="pause-follow">
              {following ? "Pause to scrub" : "Resume live"}
            </button>
            {!following && (
              <button className="btn small" onClick={() => feedRef.current?.backToLive()} data-testid="back-to-live">
                Back to live
              </button>
            )}
            <input
              type="range"
              min={0}
              max={Math.max(1, liveEdge)}
              value={Math.min(time, liveEdge)}
              onChange={(e) => void feedRef.current?.scrub(Number(e.target.value))}
              aria-label="Scrub"
              className="live-scrub"
              autoComplete="off"
            />
            <span className="mono small">
              {formatDuration(time)} / {formatDuration(liveEdge)}
            </span>
            {state.viewport && <span className="muted small">{state.viewport.w}×{state.viewport.h}</span>}
            <button
              className="btn ghost small live-fullscreen"
              onClick={() => void stageRef.current?.parentElement?.requestFullscreen?.()}
              aria-label="View session full screen"
            >
              Full screen
            </button>
          </div>
          <ControlResults state={state} onGuide={guideUser} />
          <div className="live-dock">
            <DevToolsDock fullWidth live player={player} time={time} version={version} actions={panelActions} fetchers={panelFetchers} priorEvents={priorEvents} eventsPanel={<LiveEventsPanel player={player} version={version} />} />
          </div>
        </div>
        <aside className="live-side">
          <AssistPanel
            state={state}
            disabled={status !== "open" || !!state.ended}
            onRequest={(cap) => {
              if (cap === "voice" && (voiceProbe.isPending || voiceUnavailable)) return;
              request.mutate(cap);
            }}
            onRevoke={revoke}
            microphones={microphones}
            microphoneId={microphoneId}
            onMicrophone={(deviceId) => {
              setMicrophoneId(deviceId);
              if (voice) void voice.room.setMicrophone(deviceId);
            }}
            unavailable={voiceUnavailable ? ["voice"] : []}
          />
          <CaptureHealthCard health={capture} now={now} offline={state.userOnline === false} />
          {isGranted(state, "voice") && (
            <section className="live-card live-voice" aria-label="Voice call" data-testid="voice-panel">
              <div className="live-card-head">
                <RailIcon name="voice" />
                <h3>Voice</h3>
                {voice && !voiceError && <span className="muted small">{voice.participants} on the call</span>}
              </div>
              {(voiceError || state.voiceStatus?.error) && <p className="error small">{voiceError || state.voiceStatus?.error}</p>}
              {voiceError ? (
                <div className="live-voice-row">
                  <button className="btn small" onClick={() => setVoiceAttempt((n) => n + 1)} data-testid="voice-retry">
                    Retry
                  </button>
                  <button className="btn danger small icon-only" onClick={() => revoke("voice")} data-testid="voice-hangup" aria-label="Hang up" title="Hang up">
                    <RailIcon name="hangup" />
                  </button>
                </div>
              ) : voice ? (
                <>
                {microphones.length > 1 && (
                  <select
                    className="live-voice-device"
                    aria-label="Microphone"
                    value={microphoneId}
                    onChange={(e) => {
                      const deviceId = e.target.value;
                      setMicrophoneId(deviceId);
                      void voice.room.setMicrophone(deviceId);
                    }}
                    autoComplete="off"
                  >
                    {microphones.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.label}</option>)}
                  </select>
                )}
                <div className="live-voice-row">
                  <div className="live-level" aria-label="Microphone level">
                    <div style={{ width: `${Math.round(Math.min(1, voice.level) * 100)}%` }} />
                  </div>
                  <button
                    className="btn small icon-only"
                    onClick={() => {
                      const muted = !voice.muted;
                      void voice.room.setMuted(muted);
                      setVoice({ ...voice, muted });
                    }}
                    data-testid="voice-mute"
                    aria-label={voice.muted ? "Unmute" : "Mute"}
                    title={voice.muted ? "Unmute" : "Mute"}
                  >
                    <RailIcon name={voice.muted ? "unmute" : "mute"} />
                  </button>
                  <button className="btn danger small icon-only" onClick={() => revoke("voice")} data-testid="voice-hangup" aria-label="Hang up" title="Hang up">
                    <RailIcon name="hangup" />
                  </button>
                </div>
                </>
              ) : (
                <p className="muted small">Joining…</p>
              )}
            </section>
          )}
          <ChatBox state={state} onSend={chat} disabled={status !== "open" || !!state.ended || !isGranted(state, "cobrowse")} />
          <Copilot suggestion={lastAssistant?.text} streaming={ask.streaming} onSuggest={suggest} error={ask.error} />
        </aside>
      </div>
      <SummaryDialog open={summaryOpen} onClose={() => setSummaryOpen(false)} assist={assist} state={state} startedAt={startedAt.current} sessionId={sessionId} />
    </div>
  );
}
